import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { projectService } from '@/services/projectService';
import { configService } from '@/services/configService';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { ProjectLogo } from '@/components/ProjectLogo';

export function DefautsPage() {
  const { logout } = useAuth();
  const { isRoot, isAdmin } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const projectIdInt = projectId ? Number(projectId) : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [emails, setEmails] = useState<string[]>(['']);
  const [emailErrors, setEmailErrors] = useState<Record<number, string>>({});
  const queryClient = useQueryClient();

  const { data: configData, isLoading: isLoadingConfig } = useQuery({
    queryKey: ['alert-box-offline-config'],
    queryFn: () => configService.getAlertBoxOfflineMail(),
  });

  useEffect(() => {
    if (configData?.emails && configData.emails.length > 0) {
      setEmails(configData.emails);
    }
  }, [configData]);

  const saveMutation = useMutation({
    mutationFn: (emailsToSave: string[]) => configService.updateAlertBoxOfflineMail(emailsToSave),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-box-offline-config'] });
      setEmailErrors({});
    },
  });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (email: string): boolean => {
    return emailRegex.test(email.trim());
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);

    if (emailErrors[index]) {
      const newErrors = { ...emailErrors };
      delete newErrors[index];
      setEmailErrors(newErrors);
    }
  };

  const handleAddEmail = () => {
    setEmails([...emails, '']);
  };

  const handleRemoveEmail = (index: number) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);
      const newErrors = { ...emailErrors };
      delete newErrors[index];
      setEmailErrors(newErrors);
    }
  };

  const handleSave = () => {
    const errors: Record<number, string> = {};
    const validEmails: string[] = [];

    emails.forEach((email, index) => {
      const trimmedEmail = email.trim();
      if (trimmedEmail === '') {
        return;
      }

      if (!validateEmail(trimmedEmail)) {
        errors[index] = 'Adresse email invalide';
      } else {
        validEmails.push(trimmedEmail);
      }
    });

    if (Object.keys(errors).length > 0) {
      setEmailErrors(errors);
      return;
    }

    if (validEmails.length === 0) {
      setEmailErrors({ 0: 'Au moins une adresse email est requise' });
      return;
    }

    saveMutation.mutate(validEmails);
  };

  const { data: projectsResponse, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['user-projects'],
    queryFn: () => projectService.getProjects({ page: 1, itemsPerPage: 100 }),
  });

  const userProjects = projectsResponse?.data || [];
  const currentProject = useMemo(() => {
    if (!projectIdInt) return null;
    return userProjects.find((p) => p.id === projectIdInt) ?? null;
  }, [userProjects, projectIdInt]);

  const shouldRedirectAdmin = !currentProject && projectIdInt && (isRoot() || isAdmin());
  useEffect(() => {
    if (shouldRedirectAdmin) {
      navigate(isRoot() ? '/admin/dashboard' : '/admin/users', { replace: true });
    }
  }, [shouldRedirectAdmin, isRoot, isAdmin, navigate]);

  if (!projectIdInt) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet requis</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Veuillez sélectionner un projet pour accéder aux défauts.</p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingProjects) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined animate-spin text-primary text-6xl mb-4">progress_activity</span>
            <p className="text-slate-500 dark:text-slate-400">Chargement des projets...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentProject) {
    if (isRoot() || isAdmin()) {
      return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-900 items-center justify-center">
          <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
        </div>
      );
    }
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined text-slate-400 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Aucun projet assigné</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6">Vous n'avez pas encore accès à un projet. Contactez votre administrateur pour obtenir un accès.</p>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 rounded-lg transition-colors"
            >
              <span className="material-icons-outlined">logout</span>
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>menu</span>
            </button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Défauts
            </h1>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
            <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700/50">
              <span className="material-icons-outlined mr-2 text-slate-500" style={{ fontSize: '16px' }}>domain</span>
              {currentProject.title} • {currentProject.city || 'Paris'}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="hidden md:flex items-center px-3 py-1 bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse shadow-sm shadow-green-500/50"></span>
              Système en ligne
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
            <ProjectLogo project={currentProject} size="md" className="rounded-lg" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-4xl mx-auto">
            {/* Information Card */}
            <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
                    <span className="material-icons-outlined text-primary" style={{ fontSize: '28px' }}>notifications_active</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Abonnement aux défauts</h2>
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                    Vous pouvez vous abonner aux alertes de défauts pour être informé par email lorsqu'un équipement est hors ligne.
                  </p>
                  <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <span className="material-icons-outlined text-primary mt-0.5" style={{ fontSize: '20px' }}>info</span>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Fonctionnement de l'abonnement :</p>
                        <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                          <li className="flex items-start gap-2">
                            <span className="material-icons-outlined text-primary text-base mt-0.5">check_circle</span>
                            <span>Recevez un email par jour si un équipement est hors ligne</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="material-icons-outlined text-primary text-base mt-0.5">check_circle</span>
                            <span>Les notifications sont envoyées uniquement en cas de défaut détecté</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="material-icons-outlined text-primary text-base mt-0.5">check_circle</span>
                            <span>Un résumé quotidien vous informe de tous les équipements hors ligne</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  {/* Email Configuration Section */}
                  <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-lg p-4 mb-4">
                    <div className="mb-3">
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Adresses email pour les alertes
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                        Ajoutez une ou plusieurs adresses email qui recevront les notifications quotidiennes en cas d'équipement hors ligne.
                      </p>
                    </div>

                    {isLoadingConfig ? (
                      <div className="flex items-center justify-center py-4">
                        <span className="material-icons-outlined animate-spin text-primary" style={{ fontSize: '20px' }}>progress_activity</span>
                        <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">Chargement...</span>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {emails.map((email, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="flex-1">
                              <input
                                type="email"
                                value={email}
                                onChange={(e) => handleEmailChange(index, e.target.value)}
                                placeholder="exemple@email.com"
                                className={`w-full px-3 py-2 bg-white dark:bg-slate-800 border rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 transition-colors ${
                                  emailErrors[index]
                                    ? 'border-red-500/50 focus:ring-red-500/50'
                                    : 'border-slate-300 dark:border-slate-700 focus:ring-primary/50 focus:border-primary/50'
                                }`}
                              />
                              {emailErrors[index] && (
                                <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                                  <span className="material-icons-outlined" style={{ fontSize: '14px' }}>error</span>
                                  {emailErrors[index]}
                                </p>
                              )}
                            </div>
                            {emails.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveEmail(index)}
                                className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Supprimer cette adresse"
                              >
                                <span className="material-icons-outlined" style={{ fontSize: '20px' }}>delete</span>
                              </button>
                            )}
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={handleAddEmail}
                          className="w-full px-3 py-2 bg-slate-100 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700/50 border-dashed rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:text-primary hover:border-primary/50 hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
                        >
                          <span className="material-icons-outlined" style={{ fontSize: '18px' }}>add</span>
                          Ajouter une adresse email
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 mt-4 border-t border-slate-200 dark:border-slate-700">
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {saveMutation.isSuccess && (
                          <span className="text-green-500 dark:text-green-400 flex items-center gap-1">
                            <span className="material-icons-outlined" style={{ fontSize: '14px' }}>check_circle</span>
                            Configuration enregistrée avec succès
                          </span>
                        )}
                        {saveMutation.isError && (
                          <span className="text-red-400 flex items-center gap-1">
                            <span className="material-icons-outlined" style={{ fontSize: '14px' }}>error</span>
                            Erreur lors de l'enregistrement
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saveMutation.isPending || isLoadingConfig}
                        className="px-4 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saveMutation.isPending ? (
                          <>
                            <span className="material-icons-outlined animate-spin" style={{ fontSize: '18px' }}>progress_activity</span>
                            Enregistrement...
                          </>
                        ) : (
                          <>
                            <span className="material-icons-outlined" style={{ fontSize: '18px' }}>save</span>
                            Enregistrer
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                    <span className="material-icons-outlined text-blue-400" style={{ fontSize: '20px' }}>schedule</span>
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Fréquence</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Un email de synthèse est envoyé une fois par jour, le matin, récapitulant tous les équipements hors ligne détectés.
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-green-500/20 border border-green-500/30 flex items-center justify-center">
                    <span className="material-icons-outlined text-green-400" style={{ fontSize: '20px' }}>settings</span>
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Gestion</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Vous pouvez modifier ou annuler votre abonnement à tout moment depuis cette page.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
